import { useFeatures } from "@/api/app-configuration.api";
import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { SalesStrategyCard } from "@/components/campaign/configuration/SalesStrategyCard";
import { Select, SelectItem } from "@/components/generic/Select";
import { PRIORITY_OPTIONS } from "@/data/campaign-priorities";
import { getCampaignPriority } from "@/services/configurations.service";
import { Configuration, SalesStrat } from "@/types/campaign";
import { detectCurrentStrategy } from "@/utils/configurations";
import { StepperSubmitHandler } from "@/utils/stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { SetStateAction, useEffect, useMemo } from "react";
import { Form, RadioGroup } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import gold_first from "/gold_first.png?url";
import round_robin from "/round_robin.png?url";
import silver_first from "/silver_first.png?url";

const salesStrategies: SalesStrat[] = [
  {
    id: "gold_first",
    label: "Offensiv",
    description:
      "Når du kender og er tryg ved din målgruppe, starter du ud med Guld-leads for maksimal effekt.",
    image: gold_first,
    configurations: {
      gold: { idealLeadShare: 100, priority: 1, subpriority: 1 },
      silver: { idealLeadShare: 100, priority: 2, subpriority: 1 },
      bronze: { idealLeadShare: 100, priority: 3, subpriority: 1 },
    },
  },
  {
    id: "round_robin",
    label: "Balanceret",
    description:
      "Når du over tid vil sikre forudsigelighed, fordeles Guld, Sølv og Bronze jævnt i leveringen.",
    image: round_robin,
    configurations: {
      gold: { idealLeadShare: 33, priority: 1, subpriority: 1 },
      silver: { idealLeadShare: 33, priority: 2, subpriority: 1 },
      bronze: { idealLeadShare: 33, priority: 3, subpriority: 1 },
    },
  },
  {
    id: "soft_start",
    label: "Defensiv",
    description:
      "Når du vil teste en ny målgruppe af, begynder du med Sølv-leads inden Guld brændes af.",
    image: silver_first,
    configurations: {
      gold: { idealLeadShare: 100, priority: 2, subpriority: 1 },
      silver: { idealLeadShare: 100, priority: 1, subpriority: 1 },
      bronze: { idealLeadShare: 100, priority: 3, subpriority: 1 },
    },
  },
];

const schema = z.object({
  salesStrategy: z.string(),
  priority: z.string(),
});

const getDefaultPriority = (configurations?: Configuration[]): string => {
  if (!configurations?.length) return "random";
  const { descendingOrder, orderBy } = getCampaignPriority(configurations);
  if (!orderBy) return "random";

  return (
    Object.values(PRIORITY_OPTIONS).find(
      (option) =>
        option.orderBy === orderBy &&
        option.descendingOrder === descendingOrder,
    )?.id ?? "random"
  );
};

export function SalesStrategyAndPrioritizationForm({
  configurations,
  updateConfigurations,
  setSubmitHandler,
}: {
  configurations: Configuration[];
  updateConfigurations: (
    action: SetStateAction<Configuration[]>,
  ) => Promise<void> | void;
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
}) {
  const showSalesStrategy = useMemo(
    () => configurations.filter((c) => c.enabled).length > 1,
    [configurations],
  );

  const currentSalesStrategy = useMemo(
    () => detectCurrentStrategy(configurations).label,
    [configurations],
  );
  const currentPriority = useMemo(
    () => getDefaultPriority(configurations),
    [configurations],
  );

  const defaultValues = useMemo(
    () => ({
      salesStrategy: currentSalesStrategy,
      priority: currentPriority,
    }),
    [currentSalesStrategy, currentPriority],
  );

  const { canUseContacts } = useFeatures();

  const {
    handleSubmit,
    control,
    reset,
    formState: {
      isValid,
      isDirty,
      isSubmitting,
      isSubmitted,
      defaultValues: formCurrentDefaultValues,
    },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues === formCurrentDefaultValues) return;
    reset(defaultValues, { keepValues: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const submit = useMemo(
    () =>
      handleSubmit(async (data) => {
        if (
          data.salesStrategy === currentSalesStrategy &&
          data.priority === currentPriority
        ) {
          await updateConfigurations((configurations) => configurations);
          return;
        }

        await updateConfigurations((configurations) => {
          const strategy = salesStrategies.find(
            (s) => s.id === data.salesStrategy,
          );
          if (!strategy) return configurations;

          const isRoundRobin = strategy.id === "round_robin";

          const enabledCount = isRoundRobin
            ? configurations.filter((c: Configuration) => c.enabled).length || 1
            : 0;

          const evenShare = isRoundRobin ? Math.floor(100 / enabledCount) : 0;

          const golds = configurations.filter(
            (c: Configuration) => c.quality === 1,
          );
          const silvers = configurations.filter(
            (c: Configuration) => c.quality === 2,
          );
          const bronzes = configurations.filter(
            (c: Configuration) => c.quality === 3,
          );

          const updatedConfigurations = configurations.map(
            (config: Configuration) => {
              let base, idx;

              switch (config.quality) {
                case 1:
                  base = strategy.configurations.gold;
                  idx = golds.findIndex(
                    (c: Configuration) => c.id === config.id,
                  );
                  break;
                case 2:
                  base = strategy.configurations.silver;
                  idx = silvers.findIndex(
                    (c: Configuration) => c.id === config.id,
                  );
                  break;
                case 3:
                  base = strategy.configurations.bronze;
                  idx = bronzes.findIndex(
                    (c: Configuration) => c.id === config.id,
                  );
                  break;
                default:
                  return config;
              }

              const idealLeadShare = isRoundRobin
                ? config.enabled
                  ? evenShare
                  : 0
                : base.idealLeadShare;

              return {
                ...config,
                priority: base.priority,
                subPriority: idx + 1,
                idealLeadShare,
              };
            },
          );

          let finalConfigurations = updatedConfigurations;
          if (data.priority) {
            const sel =
              PRIORITY_OPTIONS[data.priority as keyof typeof PRIORITY_OPTIONS];
            finalConfigurations = updatedConfigurations.map(
              (cfg: Configuration) => ({
                ...cfg,
                query: {
                  ...cfg.query,
                  orderBy: sel.orderBy,
                  descendingOrder: sel.descendingOrder,
                },
              }),
            );
          }

          return finalConfigurations;
        });
      }),
    [currentPriority, currentSalesStrategy, handleSubmit, updateConfigurations],
  );

  useEffect(() => {
    setSubmitHandler?.({
      submit,
      isDisabled: (!isValid && isSubmitted) || isSubmitting,
      isDirty,
      isPending: isSubmitting,
    });
  }, [setSubmitHandler, submit, isValid, isSubmitted, isSubmitting, isDirty]);

  return (
    <Form
      className="flex w-full flex-col items-center justify-between gap-y-10"
      onSubmit={submit}
    >
      {showSalesStrategy && (
        <>
          <SegmentHeader
            className="mx-auto max-w-lg"
            title="Salgsstrategi"
            description="Vælg den rigtige strategi. Skal de gode Guld-leads leveres først, vil I gemme dem til senere og starte med Sølv, eller skal de fordeles ligeligt?"
          />

          <Controller
            control={control}
            name="salesStrategy"
            render={({ field }) => (
              <RadioGroup
                className="mx-auto flex w-full max-w-2xl gap-4"
                value={field.value}
                onChange={field.onChange}
                aria-label="Vælg salgsstrategi"
              >
                {salesStrategies.map((strategy) => (
                  <SalesStrategyCard key={strategy.id} strategy={strategy} />
                ))}
              </RadioGroup>
            )}
          />
        </>
      )}

      <SegmentHeader
        className="mx-auto max-w-lg"
        title="Prioritering"
        description="Vælg i hvilken rækkefølge dine leads skal leveres."
      />

      <Controller
        control={control}
        name="priority"
        render={({ field, fieldState }) => (
          <Select
            className="w-md"
            placeholder="Vælg prioritering"
            aria-label="Vælg prioritet"
            selectedKey={field.value}
            onSelectionChange={field.onChange}
            items={Object.values(PRIORITY_OPTIONS)
              .filter((p) => p.id !== "contactHasPhoneFirst" || canUseContacts)
              .map(({ id, label }) => ({
                label,
                value: id,
              }))}
            errorMessage={fieldState?.error?.message}
          >
            {(item) => <SelectItem id={item.value}>{item.label}</SelectItem>}
          </Select>
        )}
      />
    </Form>
  );
}
